import React, { useState, useEffect } from 'react';
import { Linkedin, Mail, Moon, Sun, Menu, X, ShieldCheck, Search, Check } from 'lucide-react';
import ContactForm from './ContactForm.tsx';
import Seo from './Seo.tsx';

/**
 * Landing page for monthly maintenance and SEO (including AI search) plans.
 * Targets small and mid-size organizations and agencies needing white-label help.
 * Prices are intentionally not published; plans are scoped per site.
 */
const MaintenanceSeoPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      setIsDarkMode(mediaQuery.matches);
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const siteCare = [
    {
      heading: 'Updates, tested first',
      items: [
        'Core, theme, plugin, and dependency updates on a set schedule, monthly or bi-weekly',
        'Every update tested on a staging copy before it reaches the live site',
        'PHP and other runtimes kept on current, supported versions'
      ]
    },
    {
      heading: 'Security, backups, and uptime',
      items: [
        'Malware scan and security check with every pass',
        'User and admin account review',
        'Firewall, login hardening, and DDoS protection',
        'Daily off-site backups, plus uptime and performance monitoring'
      ]
    },
    {
      heading: 'Support',
      items: [
        'Development hours for content, layout, and feature changes, set per plan: usually a monthly bank that doesn\'t roll over, or a smaller bank you can use any time during the year',
        'Ongoing availability for bugs and outages, with roughly one-hour response on critical issues',
        'Optional managed hosting, or I work with the host you already have'
      ]
    }
  ];

  const searchSetup = [
    'Technical audit: crawlability, sitemaps, canonicals, redirects, Core Web Vitals',
    'Structured data (schema) so search engines and AI tools know exactly who you are and what you do',
    'Google Search Console, Bing Webmaster Tools, and analytics configured correctly',
    'Google Business Profile and directory cleanup: one consistent name, address, and phone number everywhere',
    'AI search readiness: structured data and clean listings so ChatGPT, Perplexity, Claude, and Google can read and cite the business, plus a periodic check of what they say about it'
  ];

  const searchMonthly = [
    'Content and page improvements aimed at the searches that bring you business',
    'Monitoring rankings, crawl errors, and directory listings',
    'Spot checks on how ChatGPT, Perplexity, and Google describe the business',
    'A short monthly report in plain language: what changed, what\'s working, what\'s next'
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Website maintenance plans',
      serviceType: 'Website maintenance',
      description: 'Monthly maintenance for WordPress, Shopify, and custom-built sites and apps: staging-tested updates, malware scans, user and admin account review, daily backups, uptime monitoring, a bank of development hours, and fast response on critical issues.',
      provider: { '@id': 'https://bensmithdev.com/#business' },
      areaServed: 'US',
      url: 'https://bensmithdev.com/maintenance-and-seo'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Search and AI search optimization',
      serviceType: 'Search engine optimization',
      description: 'Technical SEO audit, structured data, Google Business Profile and directory cleanup, and AI search readiness for ChatGPT, Perplexity, Claude, and Google AI Overviews, followed by monthly improvements and reporting.',
      provider: { '@id': 'https://bensmithdev.com/#business' },
      areaServed: 'US',
      url: 'https://bensmithdev.com/maintenance-and-seo'
    }
  ];

  const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <li className="flex items-start gap-3">
      <Check size={18} className="mt-1 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );

  // No load-time fade on this page: content painted at opacity 0 never counts
  // as Largest Contentful Paint, so Lighthouse and real-user LCP go missing.
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans">
      <Seo
        title="Website Maintenance & SEO Plans, Including AI Search | Ben Smith"
        description="Monthly maintenance and SEO plans for WordPress, Shopify, and custom-built sites and apps: tested updates, security, uptime, technical SEO, schema, Google Business Profile cleanup, and visibility in ChatGPT, Perplexity, and Google AI Overviews."
        path="/maintenance-and-seo"
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="/" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          <div className="hidden md:flex space-x-8 items-center">
            <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Home
            </a>
            <button onClick={() => scrollToSection('maintenance')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Maintenance
            </button>
            <button onClick={() => scrollToSection('seo')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              SEO
            </button>
            <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              About
            </a>
            <button onClick={() => scrollToSection('contact')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Contact
            </button>
            <button
              onClick={toggleDarkMode}
              className="transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <button
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Home</a>
              <button onClick={() => scrollToSection('maintenance')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Maintenance</button>
              <button onClick={() => scrollToSection('seo')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">SEO</button>
              <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>About</a>
              <button onClick={() => scrollToSection('contact')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Contact</button>
              <button
                onClick={() => { toggleDarkMode(); toggleMenu(); }}
                className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md flex items-center space-x-2"
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                <span>Theme</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-primary-900 text-white py-28 md:py-36 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>

          <div className="relative max-w-5xl mx-auto z-10">
            <div className="inline-block mb-6 px-4 py-1.5 bg-primary-500/20 border border-primary-400/30 rounded-full">
              <span className="text-primary-300 text-sm font-medium tracking-wide uppercase">Maintenance & SEO</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
              Keep your site healthy<br />
              <span className="text-primary-400">and easy to find.</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mb-8 text-neutral-300 leading-relaxed">
              Most small and mid-size organizations don't need an agency. They need one experienced developer who knows their site, fixes things before they break, and makes sure search engines and AI assistants understand who they are. That's what these plans are for. You work directly with me, not an account manager.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('contact')} className="bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3.5 px-8 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-center">
                Send me your URL
              </button>
              <button onClick={() => scrollToSection('maintenance')} className="bg-transparent border-2 border-white/30 text-white font-semibold py-3.5 px-8 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-center">
                See what's included
              </button>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Maintenance */}
            <div id="maintenance" className="scroll-mt-24 bg-neutral-50 dark:bg-neutral-900 p-8 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/40 rounded-xl w-fit mb-4 text-primary-600 dark:text-primary-400">
                <ShieldCheck size={24} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-primary-700 dark:text-gray-100">Maintenance</h2>
              <p className="text-neutral-500 dark:text-neutral-400 mb-6">Ongoing maintenance for WordPress, Shopify, and custom-built sites and apps.</p>
              {siteCare.map((group, gi) => (
                <div key={group.heading} className={gi < siteCare.length - 1 ? 'mb-8' : ''}>
                  <h3 className="text-sm font-semibold tracking-wide uppercase text-primary-600 dark:text-primary-400 mb-3">{group.heading}</h3>
                  <ul className="space-y-3 text-neutral-700 dark:text-neutral-300">
                    {group.items.map((item) => <ListItem key={item}>{item}</ListItem>)}
                  </ul>
                </div>
              ))}
            </div>

            {/* SEO */}
            <div id="seo" className="scroll-mt-24 bg-neutral-50 dark:bg-neutral-900 p-8 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/40 rounded-xl w-fit mb-4 text-primary-600 dark:text-primary-400">
                <Search size={24} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-primary-700 dark:text-gray-100">SEO</h2>
              <p className="text-neutral-500 dark:text-neutral-400 mb-6">A one-time setup, then monthly work.</p>
              <h3 className="text-sm font-semibold tracking-wide uppercase text-primary-600 dark:text-primary-400 mb-3">Setup</h3>
              <ul className="space-y-3 text-neutral-700 dark:text-neutral-300 mb-8">
                {searchSetup.map((item) => <ListItem key={item}>{item}</ListItem>)}
              </ul>
              <h3 className="text-sm font-semibold tracking-wide uppercase text-primary-600 dark:text-primary-400 mb-3">Monthly</h3>
              <ul className="space-y-3 text-neutral-700 dark:text-neutral-300">
                {searchMonthly.map((item) => <ListItem key={item}>{item}</ListItem>)}
              </ul>
            </div>
          </div>
          <p className="max-w-5xl mx-auto mt-10 text-center text-neutral-600 dark:text-neutral-400">
            Plans are scoped to the site. Maintenance runs on twelve-month terms, SEO on six-month terms.
          </p>
        </section>

        {/* Who it's for */}
        <section id="audience" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Who it's for
            </h2>
            <div className="space-y-6 text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed">
              <p>
                Practices, agencies, banks, destinations, and businesses that have outgrown "set it and forget it" but don't want a big-agency retainer. I've built and maintained sites for banks, universities, destination marketing organizations, and agencies since 2010.
              </p>
              <p>
                Medical practices have a few extra pieces. I clean up the Google Business Profile and the healthcare directories patients check, like Healthgrades, Zocdoc, Vitals, WebMD, and the ones specific to your specialty. I add schema for the practice, each physician, and each location so search engines and AI tools can read who you are and where you see patients. Forms and booking flows stay HIPAA-compliant, and I keep an eye on how ChatGPT, Gemini, and Perplexity describe the practice.
              </p>
              <p>
                I also work white-label for agencies that need a dependable developer behind the scenes.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-24 px-6 bg-primary-50 dark:bg-neutral-800">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-primary-700 dark:text-gray-100">Want to see where your site stands?</h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
              Send me the URL and I'll take a quick look before we talk.
            </p>
            <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-primary-100 dark:border-neutral-700">
              <ContactForm />
            </div>
            <div className="text-center mt-12">
              <p className="text-lg dark:text-neutral-200">Or email me directly:</p>
              <a href="mailto:ben@bensmithdev.com" className="text-primary-600 dark:text-primary-400 font-semibold hover:text-accent-600 dark:hover:text-accent-400 transition-colors duration-200">ben@bensmithdev.com</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-neutral-900 dark:bg-black text-white py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-6 md:mb-0">
                <a href="/" className="text-2xl font-bold">
                  <span className="text-white">Ben Smith</span>
                  <span className="text-orange-500"> Development</span>
                </a>
                <p className="mt-2 text-primary-200 dark:text-primary-300">Maintenance and SEO for websites and web apps.</p>
              </div>
              <div className="flex space-x-6">
                <a
                  href="https://www.linkedin.com/in/benjaminchristophersmith/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:ben@bensmithdev.com"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="Email ben@bensmithdev.com"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
            <div className="border-t border-primary-700/30 dark:border-primary-600/30 mt-8 pt-8 text-center text-primary-300 dark:text-primary-400">
              <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default MaintenanceSeoPage;
